"use client";

import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { useCallback, useEffect, useRef, useState } from "react";

import { TRIPLE_PIXEL_SNIPPET } from "@/constants/triplePixel";

// The same Cookiebot events the TikTok pixel in the root layout keys off, plus the decline event so a withdrawal mid-session reaches the pixel.
const COOKIEBOT_CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnLoad",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
];

export function TriplePixel({ userEmail }: { userEmail: string | null }) {
  const pathname = usePathname();
  const searchQuery = useSearchParams().toString();
  const previousPageUrl = useRef<string | null>(null);
  const [isPixelReady, setIsPixelReady] = useState(false);
  const handlePixelReady = useCallback(() => {
    setIsPixelReady(true);
  }, []);
  // Current Cookiebot marketing consent, false until Cookiebot has loaded and reported one.
  const [hasMarketingConsent, setHasMarketingConsent] = useState(false);
  // Latches on the first consent: an injected script cannot be unloaded, so a later withdrawal is signalled to the pixel instead of unmounting it.
  const [hasLoadedPixel, setHasLoadedPixel] = useState(false);

  useEffect(() => {
    const applyMarketingConsent = () => {
      if (
        window.Cookiebot === undefined ||
        window.Cookiebot.consent === undefined
      ) {
        return;
      }

      setHasMarketingConsent(window.Cookiebot.consent.marketing);
    };

    for (const eventName of COOKIEBOT_CONSENT_EVENTS) {
      window.addEventListener(eventName, applyMarketingConsent);
    }

    // Apply immediately if consent already available
    if (window.Cookiebot !== undefined) {
      applyMarketingConsent();
    }

    return () => {
      for (const eventName of COOKIEBOT_CONSENT_EVENTS) {
        window.removeEventListener(eventName, applyMarketingConsent);
      }
    };
  }, []);

  useEffect(() => {
    if (hasMarketingConsent) {
      setHasLoadedPixel(true);
    }
  }, [hasMarketingConsent]);

  useEffect(() => {
    if (!isPixelReady || window.TriplePixel === undefined) {
      return;
    }

    // Triple Whale's documented opt-out signal: a withdrawal after the pixel has loaded stops it attributing this visitor, and a later accept turns it back on.
    window.TriplePixel("trackingConsent", hasMarketingConsent);
  }, [isPixelReady, hasMarketingConsent]);

  useEffect(() => {
    if (
      isPixelReady &&
      hasMarketingConsent &&
      userEmail !== null &&
      window.TriplePixel !== undefined
    ) {
      window.TriplePixel("Contact", { email: userEmail });
    }
  }, [isPixelReady, hasMarketingConsent, userEmail]);

  useEffect(() => {
    if (
      !isPixelReady ||
      pathname === null ||
      window.TriplePixel === undefined
    ) {
      return;
    }

    const currentPageUrl = `${pathname}?${searchQuery}`;
    const lastPageUrl = previousPageUrl.current;
    previousPageUrl.current = currentPageUrl;

    // The loader records the initial view; advancing the URL even without consent prevents replaying visits made during a withdrawal.
    if (
      hasMarketingConsent &&
      lastPageUrl !== null &&
      lastPageUrl !== currentPageUrl
    ) {
      window.TriplePixel("pageLoad");
    }
  }, [isPixelReady, hasMarketingConsent, pathname, searchQuery]);

  if (!hasLoadedPixel) {
    return null;
  }

  return (
    <>
      <link
        rel="preconnect dns-prefetch"
        href="https://api.config-security.com/"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect dns-prefetch"
        href="https://conf.config-security.com/"
        crossOrigin="anonymous"
      />
      <Script
        id="triple-pixel"
        strategy="afterInteractive"
        onReady={handlePixelReady}
      >
        {TRIPLE_PIXEL_SNIPPET}
      </Script>
    </>
  );
}
